import { Paragraph } from "@albertine/design-system/components/Paragraph";
import {
    PhilanthropySearchParams,
    parsePhilanthropyParentControlMessageData,
} from "@albertine/types";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useAlbertineTranslation, useTenant } from "albertine-shared-web";
import { ArrowLeft } from "lucide-react";
import { StorySlideshowParentVisibilityProvider } from "../-components/story-slideshow/StorySlideshowParentVisibilityProvider";
import { StorySlideshowView } from "../-components/story-slideshow/StorySlideshowView";
import type { StorySlideshowParentControl } from "../-components/story-slideshow/storySlideshowTypes";
import { useStorySlideshowDocumentLock } from "../-components/story-slideshow/useStorySlideshowDocumentLock";
import {
    type PhilanthropySlide,
    PhilanthropySlideText,
} from "./-components/PhilanthropySlideText";
import { philanthropyConfigs } from "./-config";
import { usePhilanthropyRoute } from "./-hooks/usePhilanthropyRoute";

export const Route = createFileRoute("/philanthropy/")({
    validateSearch: PhilanthropySearchParams,
    component: RouteComponent,

    head: ({ match }) => {
        const { tenant, locale } = match.search;
        return {
            meta: [
                {
                    title: `Philanthropy - ${tenant} ${locale}`,
                },
            ],
        };
    },

    beforeLoad: () => {
        document.documentElement.style.setProperty("--color-body-top", "transparent");
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            "transparent",
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const t = useAlbertineTranslation();
    const { tenant } = useTenant();
    const { variant, slide: initialSlideId } = Route.useSearch();
    const { handleClose, handleSheetDismissDrag } = usePhilanthropyRoute();
    useStorySlideshowDocumentLock();
    const slides: PhilanthropySlide[] = philanthropyConfigs[tenant].slides.map(
        (slide) => ({
            id: slide.id,
            imageUrl: slide.image,
            imageAlt: t(slide.title),
            tag: t(slide.tag),
            title: t(slide.title),
            paragraphs: slide.paragraphs.map((paragraph) => t(paragraph)),
        }),
    );

    if (slides.length === 0) {
        return (
            <div className="grid h-dvh place-items-center bg-surface-inverse px-24 text-center">
                <Paragraph color="inverse-primary">
                    {t("philanthropy.unavailable")}
                </Paragraph>
            </div>
        );
    }

    return (
        <StorySlideshowParentVisibilityProvider
            parseParentControl={parsePhilanthropyParentControl}
        >
            <StorySlideshowView
                slides={slides}
                variant={variant}
                layout="fullBleed"
                initialSlideIndex={Math.max(
                    0,
                    slides.findIndex((slide) => slide.id === initialSlideId),
                )}
                labels={{
                    close: t("philanthropy.close"),
                    previous: t("philanthropy.previous"),
                    next: t("philanthropy.next"),
                }}
                closeIcon={ArrowLeft}
                renderText={(slide, direction) => (
                    <PhilanthropySlideText
                        slides={slides}
                        slide={slide}
                        direction={direction}
                    />
                )}
                onClose={handleClose}
                onSheetDismissDrag={handleSheetDismissDrag}
            />
        </StorySlideshowParentVisibilityProvider>
    );
}

function parsePhilanthropyParentControl(
    data: unknown,
): StorySlideshowParentControl | null {
    const message = parsePhilanthropyParentControlMessageData(data);

    if (!message) {
        return null;
    }

    switch (message.type) {
        case "philanthropy.visibility":
            return { type: "visibility", visible: message.visible };
        case "philanthropy.reset":
            return { type: "reset", slideId: message.slideId };
        default: {
            const unhandledMessage: never = message;
            return unhandledMessage;
        }
    }
}
